import { useMemo } from 'react';
import type { Site } from '../types';

/** Whether the page uses the photo header. Without an image it falls back to the designed banner. */
export const hasPhotoHeader = (site: Site): boolean =>
  site.heroMode === 'image' && !!site.heroImage;

/** Pseudo-random but stable tile art, so the designed banner looks the same on every load. */
function useTiles() {
  return useMemo(() => {
    let seed = 11;
    const rnd = () => {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    };
    const colors = ['#0adbad', '#4bb0dd', '#f2648b', '#fabd0a', '#ffffff', '#2c5a86'];
    const out: {
      x: number;
      y: number;
      t: number;
      rot: number;
      col: string;
      spin: boolean;
      i: number;
    }[] = [];
    const S = 64;
    for (let r = 0; r < 5; r++) {
      for (let c = 0; c < 10; c++) {
        if (rnd() < 0.2) continue;
        out.push({
          x: c * S,
          y: r * S,
          t: Math.floor(rnd() * 6),
          rot: Math.floor(rnd() * 4) * 90,
          col: colors[Math.floor(rnd() * colors.length)]!,
          spin: rnd() < 0.1,
          i: out.length,
        });
      }
    }
    return out;
  }, []);
}

function Shape({ t, col }: { t: number; col: string }) {
  const op = col === '#ffffff' ? 0.14 : 1;
  switch (t) {
    case 0:
      return <path d="M4 4H60A56 56 0 0 1 4 60Z" fill={col} opacity={op} />;
    case 1:
      return <path d="M4 34A28 28 0 0 1 60 34Z" fill={col} opacity={op} />;
    case 2:
      return <circle cx="32" cy="32" r="25" fill={col} opacity={op} />;
    case 3:
      return (
        <circle cx="32" cy="32" r="21" fill="none" stroke={col} strokeWidth="9" opacity={op} />
      );
    case 4:
      return (
        <g fill={col} opacity={op}>
          <rect x="6" y="8" width="52" height="9" rx="4.5" />
          <rect x="6" y="27" width="52" height="9" rx="4.5" />
          <rect x="6" y="46" width="52" height="9" rx="4.5" />
        </g>
      );
    default:
      return <rect x="6" y="6" width="52" height="52" rx="16" fill={col} opacity={op} />;
  }
}

function BannerArt() {
  const tiles = useTiles();
  return (
    <svg
      className="banner-art"
      viewBox="0 0 640 320"
      preserveAspectRatio="xMinYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      {tiles.map((t) => (
        <g key={t.i} transform={`translate(${t.x} ${t.y})`}>
          <g className="tile" style={{ animationDelay: t.i * 22 + 'ms' }}>
            <g className={t.spin ? 'spin' : ''}>
              <g transform={`rotate(${t.rot} 32 32)`}>
                <Shape t={t.t} col={t.col} />
              </g>
            </g>
          </g>
        </g>
      ))}
    </svg>
  );
}

export function HeroBanner({ site, live }: { site: Site; live: boolean }) {
  if (hasPhotoHeader(site)) {
    const m = site.heroMobile;
    return (
      <div
        className={'banner-photo' + (live ? ' live' : '') + (m ? ' has-m' : '')}
        style={site.heroBg ? { background: site.heroBg } : undefined}
      >
        {m && (
          <img className="hp-m" src={m} alt={site.heroAlt || `${site.fairName} של ${site.org}`} />
        )}
        <img
          className="hp-d"
          src={site.heroImage}
          alt={site.heroAlt || `${site.fairName} של ${site.org}`}
        />
      </div>
    );
  }
  return (
    <section className="banner" aria-labelledby="fair-title">
      <BannerArt />
      <div className="wrap banner-in">
        <div className="banner-text">
          <span className="b-eyebrow">{site.org} · בהפקת קודקס</span>
          <h1 id="fair-title">
            {site.fairName}
            <span>של {site.org}</span>
          </h1>
          {site.tagline && <p className="b-tag">{site.tagline}</p>}
        </div>
      </div>
    </section>
  );
}
